import {
  getAgelessFlowAccessForPlatform,
  getPlatformBySubdomain,
  getPlatformThemeBySubdomain,
} from "@/core/api/orval/ageless-companies/ageless-companies";
import { getErrorStatus, isStatus } from "@/core/api/http-error.utils";
import type { DisplayAgelessFlowAccessForPlatformResponse } from "@/core/api/orval/model";
import { AgelessAssessmentFlowType } from "@/core/api/orval/model";
import { logger } from "@/core/logging";

const NETWORK_RETRY_ATTEMPTS = 2;
const NETWORK_RETRY_DELAY_MS = 250;

const delay = async (ms: number) => {
  await new Promise((resolve) => setTimeout(resolve, ms));
};

async function retryNetworkCall<T>(
  operation: () => Promise<T>,
  context: Record<string, unknown>,
  operationName: string,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= NETWORK_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (typeof getErrorStatus(error) === "number") {
        throw error;
      }

      lastError = error;
      const isLastAttempt = attempt === NETWORK_RETRY_ATTEMPTS;
      logger.warn(`${operationName}_network_retry_attempt`, {
        ...context,
        attempt: attempt + 1,
        maxAttempts: NETWORK_RETRY_ATTEMPTS + 1,
        willRetry: !isLastAttempt,
        errorName: error instanceof Error ? error.name : "UnknownError",
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      if (isLastAttempt) {
        break;
      }

      await delay(NETWORK_RETRY_DELAY_MS * (attempt + 1));
    }
  }

  throw lastError as Error;
}

/**
 * Fetches a platform by subdomain slug.
 *
 * Returns `null` ONLY for an authoritative 404 (subdomain does not map to any
 * platform). Every other error — 5xx, network failures, parsing errors — throws
 * so the route's `error.tsx` boundary can render a real error UI. Callers should
 * treat `null` as "this subdomain isn't ours" and redirect accordingly, NOT as
 * a catch-all for transient backend failure.
 */
export async function fetchPlatform(subdomainSlug: string) {
  try {
    const platform = await retryNetworkCall(
      () => getPlatformBySubdomain({ subdomain: subdomainSlug }),
      { subdomainSlug },
      "fetch_platform",
    );
    return platform;
  } catch (error) {
    const status = getErrorStatus(error);

    if (isStatus(error, 404)) {
      logger.error("platform_not_found", { subdomainSlug, status: 404 }, error as Error);
      return null;
    }

    if (typeof status === "number") {
      logger.error("platform_fetch_http_error", { subdomainSlug, status }, error as Error);
      throw error;
    }

    logger.error("platform_fetch_network_error", { subdomainSlug }, error as Error);
    throw error;
  }
}

export type PlatformTheme = {
  brandColor: string | null;
  brandTint: string | null;
  platformName: string;
  logoImageUrl: string | null;
  websiteUrl: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  twitterUrl: string | null;
  tikTokUrl: string | null;
};

export async function fetchPlatformTheme(subdomainSlug: string): Promise<PlatformTheme | null> {
  try {
    const response = await retryNetworkCall(
      () =>
        getPlatformThemeBySubdomain({
          subdomain: subdomainSlug,
        }),
      { subdomainSlug },
      "fetch_platform_theme",
    );

    return {
      brandColor: response.brandColor ?? null,
      brandTint: response.brandTint ?? null,
      platformName: response.platformName,
      logoImageUrl: response.logoImageUrl ?? null,
      websiteUrl: response.websiteUrl ?? null,
      instagramUrl: response.instagramUrl ?? null,
      facebookUrl: response.facebookUrl ?? null,
      twitterUrl: response.twitterUrl ?? null,
      tikTokUrl: response.tikTokUrl ?? null,
    };
  } catch (error) {
    logger.error("platform_theme_fetch_error", { subdomainSlug }, error as Error);
    return null;
  }
}

/**
 * Fetches the activation/onboarding status for a given flow on a platform.
 *
 * Always throws on transport errors so the route's `error.tsx` boundary can render
 * a real error UI (with a "Try Again" button). Returning `null` here would be a
 * silent fallback that the layout currently interprets as "flow is not active" and
 * redirects users to `/not-activated` — ejecting them from a flow that may in fact
 * be active. Callers should treat a successful return value as authoritative.
 */
export async function fetchFlowAccess(
  platformId: string,
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
): Promise<DisplayAgelessFlowAccessForPlatformResponse> {
  const flowAccess = await retrieveFlowAccess(platformId, subdomainSlug, flowType);

  if (!flowAccess?.isFlowActive) {
    logger.warn("flow_access_not_active", {
      platformId,
      subdomainSlug,
      flowType,
      isFlowActive: flowAccess?.isFlowActive,
      productOptionEnabled: flowAccess?.productOptionEnabled,
      hasCapabilities: flowAccess?.hasCapabilities,
      hasOnboarded: flowAccess?.hasOnboarded,
    });
  }

  return flowAccess;
}

async function retrieveFlowAccess(
  platformId: string,
  subdomainSlug: string,
  flowType: AgelessAssessmentFlowType,
): Promise<DisplayAgelessFlowAccessForPlatformResponse> {
  try {
    return await retryNetworkCall(
      () => getAgelessFlowAccessForPlatform({ platformId, flowType }),
      { platformId, subdomainSlug, flowType },
      "fetch_flow_access",
    );
  } catch (error) {
    const status = getErrorStatus(error);

    if (typeof status === "number") {
      logger.error(
        "flow_access_fetch_error",
        { platformId, subdomainSlug, flowType, status },
        error as Error,
      );
    } else {
      logger.error(
        "flow_access_network_error",
        { platformId, subdomainSlug, flowType },
        error as Error,
      );
    }

    throw error;
  }
}

/**
 * Where a visitor is sent when the flow they asked for is switched off, in order of preference. The
 * combined flow is off whenever either half is, so whichever half the practice still runs is the right
 * destination; an aesthetics-only or wellness-only practice still serves the other single flow. Lash & brow
 * is a different vertical and never stands in for the others. Decided 2026-09-18 after a live ad campaign
 * spent ten days sending every click to the not-activated screen once its practice went wellness-only.
 */
export const INACTIVE_FLOW_FALLBACKS: Readonly<
  Record<AgelessAssessmentFlowType, readonly AgelessAssessmentFlowType[]>
> = {
  [AgelessAssessmentFlowType.AestheticsWellness]: [
    AgelessAssessmentFlowType.Aesthetics,
    AgelessAssessmentFlowType.Wellness,
  ],
  [AgelessAssessmentFlowType.Aesthetics]: [AgelessAssessmentFlowType.Wellness],
  [AgelessAssessmentFlowType.Wellness]: [AgelessAssessmentFlowType.Aesthetics],
  [AgelessAssessmentFlowType.LashBrow]: [],
};

/**
 * The first fallback flow the platform can serve right now, or `null` when none can. Probes go through the
 * same transport as `fetchFlowAccess` (and throw the same way), but skip its not-active warning: a sibling
 * flow being off is expected here and must not inflate that alert.
 */
export async function resolveActiveFallbackFlow(
  platformId: string,
  subdomainSlug: string,
  requestedFlowType: AgelessAssessmentFlowType,
): Promise<AgelessAssessmentFlowType | null> {
  for (const candidate of INACTIVE_FLOW_FALLBACKS[requestedFlowType]) {
    const access = await retrieveFlowAccess(platformId, subdomainSlug, candidate);
    if (access.isFlowActive && !access.isSubscriptionPaused) {
      logger.info("flow_access_fallback_resolved", {
        platformId,
        subdomainSlug,
        requestedFlowType,
        fallbackFlowType: candidate,
      });
      return candidate;
    }
  }
  return null;
}
